import type { ModelError } from "@iot-platform/core";
import { NonRecuperableModelError } from "@iot-platform/core";
import type { NotebookDto } from "@notebook/data/dto/notebook.dto";
import { NotebookDtoSchema } from "@notebook/data/dto/notebook.dto";
import { NotebookMessage } from "@notebook/domain/models/notebook-message.model";
import { Notebook } from "@notebook/domain/models/notebook.model";
import { injectable } from "inversify";
import { err, Result } from "neverthrow";

@injectable()
export class NotebookMapper {
    map(
        notebookDto: NotebookDto,
    ): Result<Notebook, ModelError<typeof Notebook>> {
        const notebookDtoParsed = NotebookDtoSchema.safeParse(notebookDto);

        if (!notebookDtoParsed.success) {
            console.error(notebookDtoParsed.error.issues);
            return err(new NonRecuperableModelError());
        }

        const { notebook_messages } = notebookDto;

        const mappedNotebookMessages = notebook_messages.map(
            (notebookMessageDto) => {
                const notebookMessageProps = {
                    id: notebookMessageDto.id,
                    title: notebookMessageDto.title,
                    description: notebookMessageDto.description,
                    time: notebookMessageDto.time,
                    userId: notebookMessageDto.user_id,
                    userEmail: notebookMessageDto.user_email,
                    files: notebookMessageDto.files
                        ? notebookMessageDto.files.map((file) => ({
                              id: file.id,
                              url: file.url,
                          }))
                        : null,
                };

                return NotebookMessage.createWithRecovery(notebookMessageProps);
            },
        );

        const validNotebookMessages = mappedNotebookMessages.mapNotNull(
            (result) =>
                result.match(
                    (message) => message,
                    (error) => {
                        if (!error.recuperable) {
                            console.error(
                                "Non-recoverable error mapping notebook message:",
                                error,
                            );
                        }
                        return null;
                    },
                ),
        );

        return Notebook.createWithRecovery({
            id: notebookDto.id,
            stations: notebookDto.stations,
            cropType: {
                id: notebookDto.crop_type.id,
                code: notebookDto.crop_type.code,
            },
            campaign: {
                id: notebookDto.campaign.id,
                name: notebookDto.campaign.name,
                startDate: notebookDto.campaign.start_date,
                endDate: notebookDto.campaign.end_date,
                creator: {
                    id: notebookDto.campaign.creator.id,
                    email: notebookDto.campaign.creator.email,
                },
            },
            notebookMessages: validNotebookMessages,
        });
    }
}
